import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Table,
} from "reactstrap";

import { dealsRepo } from "data/repositories/dealsRepo";
import { provincesRepo } from "data/repositories/provincesRepo";
import { adminThemes } from "data/admin/domain";
import { adminPaths } from "admin/paths";
import { confirmAdminAction, sortByOption, themeColor, toDateLabel } from "admin/contentUtils";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";

const sortOptions = [
  { value: "updated-desc", label: "Son güncellenen" },
  { value: "title-asc", label: "Başlığa göre A-Z" },
  { value: "title-desc", label: "Başlığa göre Z-A" },
  { value: "start-asc", label: "Başlangıç tarihi" },
];

export default function DealsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("active");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [sortBy, setSortBy] = useState("updated-desc");

  const load = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      setItems(dealsRepo.list({ includeDeleted: true }));
    } catch (loadError) {
      setError(loadError?.message || "Fırsatlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!location.state?.status) return;
    setFlash(location.state.status);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const provinceNameById = useMemo(() => {
    const map = new Map();
    for (const p of provincesRepo.list()) map.set(p.id, p.name);
    return map;
  }, []);

  const provinceOptions = useMemo(
    () => [{ value: "", label: "Tüm iller" }, ...provincesRepo.list().map((province) => ({ value: province.id, label: province.name }))],
    []
  );

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      if (stateFilter === "active" && item.deletedAt) return false;
      if (stateFilter === "archived" && !item.deletedAt) return false;
      if (provinceFilter && item.provinceId !== provinceFilter) return false;
      if (themeFilter && item.theme !== themeFilter) return false;
      if (!query) return true;

      return [item.id, item.title, item.description, item.priceText, item.provinceId, item.theme]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });

    return sortByOption(filtered, sortBy, {
      "start-asc": (left, right) => String(left.startDate || "").localeCompare(String(right.startDate || "")),
    });
  }, [items, provinceFilter, search, sortBy, stateFilter, themeFilter]);

  const onArchive = (item) => {
    const ok = confirmAdminAction(item.title);
    if (!ok) return;

    try {
      dealsRepo.remove(item.id);
      setFlash({ color: "success", message: `${item.title} arşive alındı.` });
      load();
    } catch (archiveError) {
      setFlash({ color: "danger", message: archiveError?.message || "Fırsat arşivlenemedi." });
    }
  };

  const onRestore = (item) => {
    try {
      dealsRepo.restore(item.id);
      setFlash({ color: "success", message: `${item.title} tekrar yayına alındı.` });
      load();
    } catch (restoreError) {
      setFlash({ color: "danger", message: restoreError?.message || "Fırsat geri alınamadı." });
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStateFilter("active");
    setProvinceFilter("");
    setThemeFilter("");
    setSortBy("updated-desc");
  };

  return (
    <>
      <AdminPageHeader
        gradient="primary"
        title="Fırsat Yönetimi"
        description="Deals listesi mobile kampanya akışlarını besleyen içerikler için kullanılır."
        primaryTo={{ tag: Link, to: `/admin${adminPaths.content.deals.create}` }}
        primaryLabel="Yeni fırsat"
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-3">Liste</h3>
                <AdminListControls
                  search={search}
                  onSearch={setSearch}
                  searchPlaceholder="Başlık, il, tema veya fiyat ara"
                  filters={[
                    {
                      key: "state",
                      label: "Kayıt durumu",
                      value: stateFilter,
                      onChange: setStateFilter,
                      options: [
                        { value: "active", label: "Aktif" },
                        { value: "archived", label: "Arşiv" },
                        { value: "all", label: "Tümü" },
                      ],
                    },
                    {
                      key: "province",
                      label: "İl",
                      value: provinceFilter,
                      onChange: setProvinceFilter,
                      options: provinceOptions,
                    },
                    {
                      key: "theme",
                      label: "Tema",
                      value: themeFilter,
                      onChange: setThemeFilter,
                      options: [{ value: "", label: "Tüm temalar" }, ...adminThemes.map((theme) => ({ value: theme, label: theme }))],
                    },
                  ]}
                  sortValue={sortBy}
                  onSortChange={setSortBy}
                  sortOptions={sortOptions}
                  onReset={resetFilters}
                  resultCount={visibleItems.length}
                  totalCount={items.length}
                />
              </CardHeader>
              <CardBody>
                <AdminStateBlock
                  flash={flash}
                  onDismissFlash={() => setFlash(null)}
                  error={error}
                  onDismissError={() => setError("")}
                  loading={loading}
                  spinnerColor="primary"
                  isEmpty={!visibleItems.length}
                  emptyTitle="Fırsat bulunamadı"
                  emptyDescription="Filtreleri değiştirin veya yeni fırsat oluşturun."
                  emptyIcon="fas fa-tags"
                >
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col">Fırsat</th>
                        <th scope="col">İl</th>
                        <th scope="col">Tema</th>
                        <th scope="col">Tarih</th>
                        <th scope="col">Durum</th>
                        <th scope="col" className="text-right">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="font-weight-bold">{item.title}</div>
                            <div className="small text-muted">{item.priceText || "-"}</div>
                            <div className="small text-muted">{item.id}</div>
                          </td>
                          <td>{provinceNameById.get(item.provinceId) || item.provinceId || "-"}</td>
                          <td>
                            <Badge color={themeColor(item.theme)} pill>
                              {item.theme}
                            </Badge>
                          </td>
                          <td>
                            <div className="small">{toDateLabel(item.startDate)}</div>
                            <div className="small text-muted">{toDateLabel(item.endDate)}</div>
                          </td>
                          <td>
                            <Badge color={item.deletedAt ? "warning" : "success"} pill>
                              {item.deletedAt ? "Arşivde" : "Aktif"}
                            </Badge>
                          </td>
                          <td className="text-right">
                            <Button
                              tag={Link}
                              to={`/admin${adminPaths.content.deals.edit(item.id)}`}
                              color="primary"
                              size="sm"
                              className="mr-2"
                            >
                              Düzenle
                            </Button>
                            {item.deletedAt ? (
                              <Button color="success" outline size="sm" onClick={() => onRestore(item)}>
                                Geri al
                              </Button>
                            ) : (
                              <Button color="danger" size="sm" onClick={() => onArchive(item)}>
                                Arşivle
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </AdminStateBlock>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
